const API_HOST = import.meta.env.VITE_API_URL || 'https://image-shop-api.yingcai-huang.workers.dev';
const BASE_URL = `${API_HOST}/v1`;

const TOKEN_KEY = 'image_shop_token';

export function setAuthToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export interface ApiError {
  code: string;
  message: string;
  request_id: string;
  details?: Record<string, string>;
}

export interface ApiResponse<T> {
  data: T;
  request_id: string;
}

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public error: ApiError,
  ) {
    super(error.message);
    this.name = 'ApiRequestError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { params?: Record<string, string> },
): Promise<ApiResponse<T>> {
  const url = new URL(`${BASE_URL}${path}`);
  if (options?.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        url.searchParams.set(key, value);
      }
    });
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: { code: 'UNKNOWN', message: 'An unknown error occurred', request_id: '' },
    }));
    throw new ApiRequestError(response.status, errorData.error);
  }

  return response.json();
}

export const api = {
  get<T>(path: string, params?: Record<string, string>) {
    return request<T>('GET', path, undefined, { params });
  },
  post<T>(path: string, body?: unknown) {
    return request<T>('POST', path, body);
  },
  patch<T>(path: string, body?: unknown) {
    return request<T>('PATCH', path, body);
  },
  delete<T>(path: string) {
    return request<T>('DELETE', path);
  },
};
